import { z } from 'zod'

/**
 * Environment access, validated once at module load.
 *
 * Why a schema rather than reading `import.meta.env` at the call site:
 * a missing `VITE_SUPABASE_ANON_KEY` should fail loudly at boot with a
 * message naming the variable, not produce a mystifying 401 twenty
 * minutes into a debugging session. Failing fast on config is the
 * cheapest reliability win available.
 */
const envSchema = z.object({
  VITE_SUPABASE_URL: z.url('VITE_SUPABASE_URL must be a valid URL'),
  VITE_SUPABASE_ANON_KEY: z.string().min(20, 'VITE_SUPABASE_ANON_KEY looks empty or truncated'),

  VITE_APP_NAME: z.string().default('Legacy'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_APP_URL: z.url().default('http://localhost:5173'),

  // Vite exposes booleans as strings. Coerce explicitly rather than
  // relying on truthiness — the string "false" is truthy.
  VITE_FEATURE_AI: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  VITE_FEATURE_DNA: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  VITE_FEATURE_BOOK_EXPORT: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  const issues = parsed.error.issues.map((i) => `  • ${i.path.join('.')}: ${i.message}`).join('\n')

  throw new Error(
    `Invalid environment configuration.\n\n${issues}\n\n` +
      'Copy .env.example to .env.local and fill in the values. ' +
      'For local development, run `npm run db:start` — the Supabase CLI ' +
      'prints the URL and anon key you need.'
  )
}

export const env = parsed.data

/**
 * Feature flags. Milestones ship dark and are switched on here, so a
 * half-finished feature can be merged to main without being reachable.
 */
export const features = {
  ai: env.VITE_FEATURE_AI,
  dna: env.VITE_FEATURE_DNA,
  bookExport: env.VITE_FEATURE_BOOK_EXPORT,
} as const

export const isProduction = env.VITE_APP_ENV === 'production'
export const isDevelopment = env.VITE_APP_ENV === 'development'
